import { Button, Col, DatePicker, Form, notification, Row, Space } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';

import BieuDo from '../../components/LineChart';
import { dataToOptionSelect, exportTableToExcelNew, formatNumber, newObjectKeys } from '../../utils/utility';
import { useQueries, useQuery } from '@tanstack/react-query';
import FilterTable from '../../components/FilterTable';
import NormalFormLabel from '../../components/NormalFormLabel';
import CustomSelect from '../../components/CustomSelect';
import { useSelector } from 'react-redux';
import { checkPermission } from '../../utils/checkPermission';
import { GET_DATA_NOTIFICATION_STATISTICS, GET_GRAPHICAL_NOTIFICATION_STATISTICS } from '../../constants/apiEndPoint';
import {
  getDataStatisticNotificationRequest,
  getGraphicalStatisticNotificationRequest,
  getListAllCampaignsLichVietRequest,
  getListAllNotificationsRequest,
  getListDimensionsNotificationRequest,
  getListMetricsNotificationRequest,
  getListNotificationAudidencesRequest,
  getListNotificationLabelsRequest,
  getListNotificationTypesRequest,
} from '../../services/notificationStatisticRequest';
import { getListVersionAndTopicRequest } from '../../services/commonRequest';
import { isEqual } from 'lodash';

const rangePresets = [
  {
    label: '7 ngày trước',
    value: [dayjs().subtract(7, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '14 ngày trước',
    value: [dayjs().subtract(14, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '30 ngày trước',
    value: [dayjs().subtract(30, 'days'), dayjs().subtract(1, 'days')],
  },
  {
    label: '90 ngày trước',
    value: [dayjs().subtract(90, 'days'), dayjs().subtract(1, 'days')],
  },
];

export default function ThongKeThongBao() {
  const [formSearch] = Form.useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingExportData, setIsLoadingExportData] = useState(false);

  const dataRedux = useSelector((state) => state.user);
  const accountPartnerId = dataRedux?.info?.partner_id;
  const accountType = dataRedux?.info?.type;
  const checkAccountIsPartner = accountType === 2 && accountPartnerId;

  const watchCampaignLichVietIds = Form.useWatch('campaign_ids', formSearch);
  const watchNotificationType = Form.useWatch('notification_type_ids', formSearch);

  useEffect(() => {
    formSearch.setFieldValue('notification_ids', undefined);
  }, [watchCampaignLichVietIds]);

  const [params, setParams] = useState({
    start_time: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
    end_time: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
    campaign_ids: [],
    notification_type_ids: [],
    audience_codes: [],
    notification_ids: [],
    labels: [],
    app_versions: [],
  });

  const [filterDimensionAndMetric, setFilterDimensionAndMetric] = useState({
    pageId: 1,
    limit: 20,
    dimension_ids: [1, 2, 3],
    metric_ids: [1, 2, 3, 4, 5],
    order_by_conditions: [],
  });

  const [
    { data: dataAllCampaignsLichViet },
    { data: dataAllNotifications },
    { data: dataNotificationTypes },
    { data: dataAllAudiences },
    { data: dataDimensionsNotification },
    { data: dataMetricsNotification },
    { data: dataAllLabels },
    { data: dataAllVersions },
  ] = useQueries({
    queries: [
      {
        queryKey: ['dataAllCampaignsLichVietStatistic'],
        queryFn: () => getListAllCampaignsLichVietRequest(),
      },
      {
        queryKey: ['dataAllNotificationsStatistic', watchCampaignLichVietIds, watchNotificationType],
        queryFn: () =>
          getListAllNotificationsRequest({
            notification_type: watchNotificationType,
            campaign_ids: watchCampaignLichVietIds,
          }),
      },
      {
        queryKey: ['dataNotificationTypesStatistic'],
        queryFn: () => getListNotificationTypesRequest(),
      },
      {
        queryKey: ['dataAllAudiencesStatistic'],
        queryFn: () => getListNotificationAudidencesRequest(),
      },
      {
        queryKey: ['dataDimensionsNotificationStatistic'],
        queryFn: () => getListDimensionsNotificationRequest(),
      },
      {
        queryKey: ['dataMetricsNotificationStatistic'],
        queryFn: () => getListMetricsNotificationRequest(),
      },
      {
        queryKey: ['dataAllLabelsStatistic'],
        queryFn: () => getListNotificationLabelsRequest(),
      },
      {
        queryKey: ['dataAllVersionsStatistic'],
        queryFn: () => getListVersionAndTopicRequest({ type: 2 }),
      },
    ],
  });

  const sortedVersions = useMemo(() => {
    if (!dataAllVersions) return [];
    return [...dataAllVersions].sort((a, b) => {
      const partsA = (a?.name || '').split(/[-.]/);
      const partsB = (b?.name || '').split(/[-.]/);
      const maxLength = Math.max(partsA.length, partsB.length);
      for (let i = 0; i < maxLength; i++) {
        const partA = partsA[i];
        const partB = partsB[i];
        if (partA === undefined) return 1;
        if (partB === undefined) return -1;

        const numA = parseInt(partA, 10);
        const numB = parseInt(partB, 10);

        if (!isNaN(numA) && !isNaN(numB)) {
          if (numA !== numB) {
            return numB - numA;
          }
        } else {
          if (partA !== partB) {
            return partB.localeCompare(partA);
          }
        }
      }
      return 0;
    });
  }, [dataAllVersions]);

  const {
    data: dataStatisticNotifications,
    refetch: refetchDataStatisticNotifications,
    isPending: isPendingDataStatisticNotifications,
  } = useQuery({
    queryKey: ['dataStatisticCampaigns', { ...params, ...filterDimensionAndMetric }],
    queryFn: () =>
      getDataStatisticNotificationRequest({
        ...params,
        ...filterDimensionAndMetric,
        page: filterDimensionAndMetric?.pageId,
        pageId: undefined,
      }),
  });

  const handleSubmitForm = (values) => {
    const temp = {};

    if (values?.timeRange) {
      Object.assign(temp, {
        start_time: dayjs(values?.timeRange?.[0]).format('YYYY-MM-DD'),
        end_time: dayjs(values?.timeRange?.[1]).format('YYYY-MM-DD'),
      });
    }

    if (values?.campaign_ids) {
      Object.assign(temp, { campaign_ids: values?.campaign_ids });
    } else {
      Object.assign(temp, { campaign_ids: [] });
    }

    if (values?.notification_type_ids) {
      Object.assign(temp, { notification_type_ids: [values?.notification_type_ids] });
    } else {
      Object.assign(temp, { notification_type_ids: [] });
    }

    if (values?.audience_codes) {
      Object.assign(temp, { audience_codes: values?.audience_codes });
    } else {
      Object.assign(temp, { audience_codes: [] });
    }

    if (values?.notification_ids) {
      Object.assign(temp, { notification_ids: values?.notification_ids });
    } else {
      Object.assign(temp, { notification_ids: [] });
    }

    if (values?.labels) {
      Object.assign(temp, { labels: values?.labels });
    } else {
      Object.assign(temp, { labels: [] });
    }

    if (values?.app_versions) {
      Object.assign(temp, { app_versions: values?.app_versions });
    } else {
      Object.assign(temp, { app_versions: [] });
    }

    if (isEqual(temp, params)) {
      refetchDataStatisticNotifications();
    }

    setParams(temp);
    setFilterDimensionAndMetric({ ...filterDimensionAndMetric, pageId: 1, limit: 20 });
  };

  const handleExportData = async () => {
    setIsLoadingExportData(true);
    const dataExport = await getDataStatisticNotificationRequest({
      ...params,
      ...filterDimensionAndMetric,
      page: 1,
      pageId: undefined,
      limit: undefined,
    });

    if (!dataExport?.data?.statistics || dataExport?.data?.statistics.length === 0) {
      notification.error({
        message: 'Xuất file dữ liệu thất bại. Vui lòng thử lại sau!',
      });
      setIsLoadingExportData(false);
      return;
    }

    let dataExportTmp = [];

    dataExport?.data?.statistics?.forEach((statistic, i) => {
      const value = statistic.reduce((acc, item) => {
        acc[item.name] = formatNumber(item.value);
        acc.type = item.type;
        acc.id = item.id;
        acc.key = i;
        return acc;
      }, {});
      dataExportTmp.push(value);
    });

    const fixedColumns = dataExport?.data?.total || [];
    const colSpan = dataExport?.data?.statistics[0].filter((item) => item.type === 'dimension')?.length || 0;

    const dataExportFinal = {
      data: dataExportTmp,
      fixedColumns: fixedColumns,
      colSpan: colSpan,
    };

    exportTableToExcelNew(
      dataExportFinal,
      `Thongkethongbao_tungay_${dayjs(params?.start_time).format('DD-MM-YYYY')}_denngay_${dayjs(
        params?.end_time
      ).format('DD-MM-YYYY')}`
    );
    setIsLoadingExportData(false);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="font-semibold text-2xl font-roboto">Thống kê thông báo</h1>
        <div className="flex justify-end gap-2">
          <Button loading={isLoadingExportData} type="primary" onClick={() => handleExportData()}>
            <DownloadOutlined /> Export
          </Button>

          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            <PlusOutlined /> Thêm biểu đồ
          </Button>
        </div>
      </div>

      <Form layout="vertical" name="search-notification-statistics" form={formSearch} onFinish={handleSubmitForm}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} lg={6}>
            <Form.Item
              label={<NormalFormLabel text="Thời gian" />}
              name="timeRange"
              initialValue={[dayjs(params?.start_time, 'YYYY-MM-DD'), dayjs(params?.end_time, 'YYYY-MM-DD')]}
            >
              <DatePicker.RangePicker
                placeholder={['Từ ngày', 'Đến ngày']}
                format="DD-MM-YYYY"
                presets={rangePresets}
                allowClear={false}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Chiến dịch" />} name="campaign_ids">
              <CustomSelect
                placeholder="Chọn chiến dịch"
                options={dataToOptionSelect(dataAllCampaignsLichViet || [])}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Loại thông báo" />} name="notification_type_ids">
              <CustomSelect
                placeholder="Loại thông báo"
                options={dataToOptionSelect(dataNotificationTypes || [], 'name', 'value')}
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Tập khách hàng" />} name="audience_codes">
              <CustomSelect
                placeholder="Tập khách hàng"
                options={dataToOptionSelect(dataAllAudiences || [])}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Thông báo" />} name="notification_ids">
              <CustomSelect
                placeholder="Thông báo"
                options={dataToOptionSelect(dataAllNotifications || [])}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Label" />} name="labels">
              <CustomSelect
                placeholder="Label thống kê"
                options={dataToOptionSelect(dataAllLabels || [])}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Form.Item label={<NormalFormLabel text="Phiên bản ứng dụng" />} name="app_versions">
              <CustomSelect
                placeholder="Phiên bản ứng dụng"
                options={dataToOptionSelect(sortedVersions)}
                mode="multiple"
              />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} lg={6} className="flex items-end">
            <Form.Item>
              <Space direction="horizontal">
                <Button type="primary" htmlType="submit">
                  <SearchOutlined /> Tìm kiếm
                </Button>
                {params &&
                  Object.values(
                    newObjectKeys(params, [
                      'start_time',
                      'end_time',
                      'campaign_ids',
                      'notification_type_ids',
                      'audience_codes',
                      'notification_ids',
                      'labels',
                      'app_versions',
                    ])
                  ).some((value) => (value && !Array.isArray(value)) || value?.length > 0) && (
                    <Button
                      htmlType="button"
                      onClick={() => {
                        formSearch.resetFields();
                        formSearch.setFieldValue('timeRange', [
                          dayjs().subtract(30, 'days'),
                          dayjs().subtract(1, 'days'),
                        ]);
                        setParams({
                          start_time: dayjs().subtract(30, 'days').format('YYYY-MM-DD'),
                          end_time: dayjs().subtract(1, 'days').format('YYYY-MM-DD'),
                          campaign_ids: [],
                          notification_type_ids: [],
                          audience_codes: [],
                          notification_ids: [],
                          labels: [],
                          app_versions: [],
                        });
                        setFilterDimensionAndMetric({ ...filterDimensionAndMetric, pageId: 1, limit: 20 });
                      }}
                    >
                      <DeleteOutlined />
                      Xóa lọc
                    </Button>
                  )}
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {checkPermission(GET_GRAPHICAL_NOTIFICATION_STATISTICS, 'Xem biểu đồ thống kê') && (
        <BieuDo
          keyChart="notification"
          whereCondition={params}
          apiCallBack={getGraphicalStatisticNotificationRequest}
          dataDimensions={dataDimensionsNotification}
          dataMetrics={dataMetricsNotification}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      )}

      {checkPermission(GET_DATA_NOTIFICATION_STATISTICS, 'Xem dữ liệu thống kê') && (
        <div className="flex w-full">
          <FilterTable
            tableKey="notification"
            isLoadingFilterTable={isPendingDataStatisticNotifications}
            filterDimensionAndMetric={filterDimensionAndMetric}
            setFilterDimensionAndMetric={setFilterDimensionAndMetric}
            dataDimensions={dataDimensionsNotification}
            dataMetrics={dataMetricsNotification}
            dataStatisticCampaigns={dataStatisticNotifications}
          />
        </div>
      )}
    </div>
  );
}
